import React from 'react';

const PrivacyPolicy = ({ onBack }) => {
  return (
    <div className="min-h-screen py-10 px-4" style={{ backgroundColor: 'var(--bg-base)' }}>
      <div className="max-w-3xl mx-auto">
        {/* Back button */}
        <button
          onClick={onBack}
          className="mb-6 flex items-center gap-2 text-sm font-medium transition-colors"
          style={{ color: 'var(--accent-primary)' }}
        >
          <span>←</span> Back
        </button>

        <div className="p-8 rounded-2xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
          <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Privacy Policy</h1>
          <p className="text-sm mb-1" style={{ color: 'var(--text-muted)' }}>Effective Date: February 25, 2026</p>
          <p className="text-sm mb-8" style={{ color: 'var(--text-muted)' }}>Last Updated: February 25, 2026</p>

          <div className="space-y-6 text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>

            {/* 1 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>1. Introduction</h2>
              <p>
                Founders+ ("we," "us," or "our") respects your privacy and is committed to protecting your personal data.
                This Privacy Policy explains how we collect, use, store, share, and protect your information when you use
                the Founders+ platform ("the Platform" or "Service").
              </p>
              <p className="mt-2">
                By accessing or using the Service, you confirm that you have read, understood, and agree to this Privacy
                Policy. If you do not agree, you must cease using the Service immediately.
              </p>
            </section>

            {/* 2 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>2. Information We Collect</h2>
              <h3 className="font-semibold mt-3 mb-1" style={{ color: 'var(--text-primary)' }}>2.1 Information You Provide</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Account Information:</strong> Email address, password (hashed and never stored in plain text), username, and profile details.</li>
                <li><strong>Profile Data:</strong> Bio, founder type, skills, current projects, profile images, and any optional information you add to your profile.</li>
                <li><strong>User Content:</strong> Posts, comments, reactions, and any other content you create or share on the Platform.</li>
                <li><strong>Communications:</strong> Messages, group chats, and other communications sent through the Platform's messaging features.</li>
                <li><strong>Event Data:</strong> Events you create, organize, or register interest in through the Platform.</li>
              </ul>
              <h3 className="font-semibold mt-3 mb-1" style={{ color: 'var(--text-primary)' }}>2.2 Information Collected Automatically</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Usage Data:</strong> Pages visited, features used, time spent on the Platform, and interaction patterns.</li>
                <li><strong>Device Information:</strong> Browser type, operating system, screen resolution, and device identifiers.</li>
                <li><strong>Log Data:</strong> IP addresses, access timestamps, error logs, and referring URLs.</li>
                <li><strong>Authentication Tokens:</strong> Secure tokens stored in browser local storage to maintain your session.</li>
              </ul>
              <h3 className="font-semibold mt-3 mb-1" style={{ color: 'var(--text-primary)' }}>2.3 Information from Third Parties</h3>
              <p>
                We may receive limited information from third-party services integrated with the Platform (e.g., authentication
                providers). We process such information in accordance with this Policy.
              </p>
            </section>

            {/* 3 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>3. Legal Basis for Processing</h2>
              <p>We process your personal data on the following legal bases:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li><strong>Contractual Necessity:</strong> Processing required to provide the Service you have signed up for.</li>
                <li><strong>Legitimate Interests:</strong> Improving the Platform, preventing fraud, and ensuring security.</li>
                <li><strong>Consent:</strong> Where you have explicitly consented, such as for optional communications.</li>
                <li><strong>Legal Obligation:</strong> Where we are required to process data to comply with applicable law.</li>
              </ul>
            </section>

            {/* 4 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>4. How We Use Your Information</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>To provide, maintain, operate, and improve the Platform and its features.</li>
                <li>To authenticate your identity and secure your account.</li>
                <li>To personalise your experience, including content recommendations and connection discovery.</li>
                <li>To facilitate connections, messaging, and collaboration between users.</li>
                <li>To send important notifications about your account, the Platform, or your events.</li>
                <li>To analyse usage trends and improve Platform performance and user experience.</li>
                <li>To detect, investigate, and prevent fraud, abuse, or security incidents.</li>
                <li>To comply with applicable legal obligations and respond to lawful requests.</li>
                <li>To power AI-assisted features such as momentum scoring and the AI Co-Founder Assistant.</li>
              </ul>
            </section>

            {/* 5 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>5. Data Sharing and Disclosure</h2>
              <p>
                <strong style={{ color: 'var(--text-primary)' }}>We do not sell your personal data.</strong> We may share information only in the following circumstances:
              </p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li><strong>With Other Users:</strong> Your profile information, posts, and activity are visible to other Platform users as determined by your privacy settings.</li>
                <li><strong>Service Providers:</strong> Trusted third-party vendors who help us operate the Platform (e.g., hosting infrastructure, analytics, email delivery). These providers are contractually bound to protect your data and may only use it as directed by us.</li>
                <li><strong>Legal Requirements:</strong> We may disclose information if required by applicable law, regulation, court order, or governmental request.</li>
                <li><strong>Safety and Security:</strong> To protect the rights, safety, and property of Founders+, our users, or the public where we believe disclosure is necessary.</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, restructuring, or sale of all or part of our assets, user data may be transferred as part of that transaction. You will be notified of any such change.</li>
                <li><strong>With Your Consent:</strong> In any other circumstances where you have provided explicit consent.</li>
              </ul>
            </section>

            {/* 6 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>6. Data Storage, Security, and Retention</h2>
              <h3 className="font-semibold mt-3 mb-1" style={{ color: 'var(--text-primary)' }}>6.1 Security Measures</h3>
              <p>
                We implement industry-standard security measures including encrypted connections (HTTPS), hashed passwords,
                secure authentication tokens, and access controls. However, no method of transmission or storage is 100% secure,
                and we cannot guarantee absolute security of your data.
              </p>
              <h3 className="font-semibold mt-3 mb-1" style={{ color: 'var(--text-primary)' }}>6.2 Data Retention</h3>
              <p>
                We retain your personal data for as long as your account is active or as reasonably necessary to provide the
                Service. Upon account deletion, we will delete or anonymise your personal data within a reasonable period,
                except where retention is required by law or legitimate business purposes.
              </p>
              <h3 className="font-semibold mt-3 mb-1" style={{ color: 'var(--text-primary)' }}>6.3 Account Responsibility</h3>
              <p>
                You are responsible for maintaining the confidentiality of your account credentials. We are not responsible
                for unauthorised access resulting from your failure to protect your login information.
              </p>
            </section>

            {/* 7 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>7. Your Rights</h2>
              <p>
                Depending on your jurisdiction (including under GDPR for EEA residents and CCPA for California residents),
                you may have the following rights:
              </p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li><strong>Right to Access:</strong> Request a copy of the personal data we hold about you.</li>
                <li><strong>Right to Correction:</strong> Request correction of inaccurate or incomplete data.</li>
                <li><strong>Right to Deletion:</strong> Request deletion of your personal data ("right to be forgotten").</li>
                <li><strong>Right to Portability:</strong> Request your data in a structured, machine-readable format.</li>
                <li><strong>Right to Object:</strong> Object to certain types of processing, including direct marketing.</li>
                <li><strong>Right to Restrict Processing:</strong> Request that we limit how we use your data in certain circumstances.</li>
                <li><strong>Right to Withdraw Consent:</strong> Where processing is based on consent, withdraw it at any time.</li>
                <li><strong>Right to Complain:</strong> Lodge a complaint with your local data protection supervisory authority.</li>
              </ul>
              <p className="mt-2">
                To exercise any of these rights, contact us at{' '}
                <a href="mailto:foundersplus2025@gmail.com" style={{ color: 'var(--accent-primary)' }}>foundersplus2025@gmail.com</a>.
                We will respond within fifteen (15) business days.
              </p>
            </section>

            {/* 8 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>8. Cookies and Local Storage</h2>
              <p>
                We use browser local storage to maintain your authentication session and user preferences. We may use
                analytics tools to understand how the Platform is used and to improve performance.
              </p>
              <p className="mt-2">
                You can manage or clear local storage and cookies through your browser settings. Disabling storage mechanisms
                may affect Platform functionality, including the ability to remain logged in.
              </p>
            </section>

            {/* 9 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>9. Children's Privacy</h2>
              <p>
                The Platform is not intended for users under 18 years of age. We do not knowingly collect personal data from
                minors. If you believe we have inadvertently collected data from a person under 18, please contact us
                immediately and we will take prompt steps to delete such data.
              </p>
            </section>

            {/* 10 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>10. International Data Transfers</h2>
              <p>
                Your data may be transferred to, stored in, or processed in jurisdictions other than where you reside,
                including countries that may have different data protection standards. By using the Platform, you consent
                to such transfers.
              </p>
              <p className="mt-2">
                Where required by applicable law (e.g., GDPR), we ensure that appropriate safeguards are in place for
                cross-border transfers, such as standard contractual clauses or equivalent mechanisms.
              </p>
            </section>

            {/* 11 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>11. AI Features and Data Processing</h2>
              <p>
                Certain features of the Platform, including the AI Co-Founder Assistant and momentum scoring, process
                your activity data to generate personalised insights and suggestions. This processing is performed
                to provide the Service and improve your experience. AI-generated outputs do not constitute professional
                advice of any kind.
              </p>
              <p className="mt-2">
                <strong style={{ color: 'var(--text-primary)' }}>Where your content goes.</strong> When you use AI
                features, the text you have written &mdash; which may include notes, diary entries, decisions, issues
                and tasks &mdash; is sent to Google&rsquo;s Gemini API so a response can be generated. Google acts as
                our service provider for this and processes that content on our instructions.
              </p>
              <p className="mt-2">
                We use a paid Gemini tier, under which Google does not use content submitted through the API to train
                or improve its models. We do not use your personal data to train third-party AI models, and we do not
                sell or share it for that purpose.
              </p>
              <p className="mt-2">
                If you would rather your writing were not processed this way, avoid using the AI features; the rest of
                the Platform works without them.
              </p>
            </section>

            {/* 12 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>12. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Material changes will be communicated via email
                notification to your registered account or by prominent notice on the Platform. Your continued use of
                the Platform after changes are posted constitutes acceptance of the updated Policy.
              </p>
            </section>

            {/* 13 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>13. Contact Information</h2>
              <p>For questions, concerns, or to exercise your data rights, contact us at:</p>
              <p className="mt-2">
                <strong style={{ color: 'var(--text-primary)' }}>Email:</strong>{' '}
                <a href="mailto:foundersplus2025@gmail.com" style={{ color: 'var(--accent-primary)' }}>
                  foundersplus2025@gmail.com
                </a>
              </p>
              <p className="mt-1">
                <strong style={{ color: 'var(--text-primary)' }}>Response Time:</strong> We respond to inquiries within fifteen (15) business days.
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
